import { useState } from 'react';
import {
    Box, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';

import { useSelector } from 'BMapsRedux';
import { App } from 'BMapsSrc/BMapsApp';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import { DialogPrimaryButton } from 'BMapsUI/common/DialogActionButtons';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import { wrapForDisabledTooltip } from 'BMapsSrc/ui/common/helpers';
import { applyFepScoringResults, fepCandidateCompounds } from 'BMapsSrc/integrations/fep';
import { ImportErrors } from '../../ImportPane/import_components';
import { openFreeEnergySidePanel } from '../FreeEnergySidePanel';

function fmt(value, digits=2) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '';
    return Number(value).toFixed(digits);
}

function fmtPm(value, err) {
    const v = fmt(value);
    if (!v) return '';
    const e = fmt(err);
    return e ? `${v} ± ${e}` : v;
}

/**
 * Results of the free-energy jobs chosen in the Jobs tab: per-ligand ΔG (with
 * uncertainty and whether the ligand is still in the workspace), per-edge
 * ΔΔG, the run's status line and warning, and an Apply action that writes ΔG
 * onto the compounds the job was submitted for.
 */
export default function FreeEnergyResults({ selected }) {
    const { localJobState, displayedFreeEnergyJobs, jobs } = useSelector((state) => state.jobs);

    if (!selected) return null;

    const displayed = (displayedFreeEnergyJobs || [])
        .map((job) => jobs.find((j) => j.jobid === job.jobid) || job);

    if (displayed.length === 0) {
        return (
            <DialogContentSection title="Free Energy Results" disablePaddingTop>
                <span>
                    No free-energy results to display.
                    <br />
                    Load results from the
                    {' '}
                    <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
                        component="button"
                        onClick={() => openFreeEnergySidePanel('fep_jobs_tab')}
                        sx={{ color: 'var(--marketing-blue)' }}
                    >
                        Jobs tab
                    </Link>
                    {' '}
                    to view them here.
                </span>
            </DialogContentSection>
        );
    }

    return (
        <Box sx={{ overflow: 'auto', height: '100%' }}>
            {displayed.map((job) => (
                <JobResults key={job.jobid} job={job} local={localJobState[job.jobid] || {}} />
            ))}
        </Box>
    );
}

function JobResults({ job, local }) {
    const [applying, setApplying] = useState(false);
    const [applyErrors, setApplyErrors] = useState();
    const summary = local.results;
    const title = job.requestmeta?.jobName || job.jobid;
    const method = job.requestmeta?.method || job.jobtype;

    if (!summary) {
        return (
            <DialogContentSection title={title} titleVariant="subtitle1">
                <span>{`${method}: results not loaded${local.error ? ` (${local.error})` : ''}.`}</span>
            </DialogContentSection>
        );
    }

    const loadedNames = new Set(App.Workspace.getLoadedCompounds().map((c) => c.resSpec));
    // The stash arrives with the results (kept on the local job state);
    // before that the job list's request metadata carries the same scope.
    const candidates = fepCandidateCompounds(local.stash ? { stash: local.stash } : job);
    const scores = summary.scores || [];
    const edges = summary.edges || [];
    const applied = local.applied || [];
    const missing = local.missing || [];

    const statusLine = [
        method,
        summary.status ? `status ${summary.status}` : null,
        (summary.total != null) ? `${summary.finished ?? 0} of ${summary.total} work units finished` : null,
        summary.failed ? `${summary.failed} failed` : null,
        job.requestmeta?.target ? `on ${job.requestmeta.target}` : null,
    ].filter(Boolean).join(' · ');

    function workspaceState(name) {
        if (!loadedNames.has(name)) return 'no';
        return applied.includes(name) ? 'applied' : 'yes';
    }

    let applyDisabled = '';
    if (scores.length === 0) applyDisabled = 'This job produced no per-ligand ΔG to apply';
    else if (candidates.length === 0) applyDisabled = 'None of the job\'s ligands are loaded in the workspace';

    async function apply() {
        setApplying(true);
        setApplyErrors(undefined);
        try {
            const { error } = await applyFepScoringResults(job.jobid);
            if (error) setApplyErrors([error]);
        } finally {
            setApplying(false);
        }
    }

    return (
        <DialogContentSection title={title} titleVariant="subtitle1" collapsible defaultExpand>
            <Typography component="div" sx={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                {statusLine}
            </Typography>
            {summary.warning && (
                <Typography component="div" sx={{ fontSize: '0.9rem', marginBottom: '0.5rem', color: 'var(--marketing-orange, #b8650c)' }}>
                    {summary.warning}
                </Typography>
            )}

            <Typography component="div" sx={{ fontWeight: 600, marginTop: '0.5rem' }}>
                {`Per-ligand ΔG (${scores.length})`}
            </Typography>
            {scores.length > 0 && (
                <TableContainer sx={{ marginBottom: '0.5rem' }}>
                    <Table size="small" aria-label="Per-ligand binding free energy">
                        <TableHead>
                            <TableRow>
                                <TableCell>Ligand</TableCell>
                                <TableCell align="right">ΔG (kcal/mol)</TableCell>
                                <TableCell>In workspace</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[...scores].sort((a, b) => (a.dg ?? 0) - (b.dg ?? 0)).map((s) => (
                                <TableRow key={s.name}>
                                    <TableCell>{s.name}</TableCell>
                                    <TableCell align="right">{fmtPm(s.dg, s.dg_err)}</TableCell>
                                    <TableCell>{workspaceState(s.name)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {edges.length > 0 && (
                <>
                    <Typography component="div" sx={{ fontWeight: 600, marginTop: '0.5rem' }}>
                        {`Per-edge ΔΔG (${edges.length})`}
                    </Typography>
                    <TableContainer sx={{ marginBottom: '0.5rem' }}>
                        <Table size="small" aria-label="Per-edge relative binding free energy">
                            <TableHead>
                                <TableRow>
                                    <TableCell>From</TableCell>
                                    <TableCell>To</TableCell>
                                    <TableCell align="right">ΔΔG (kcal/mol)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {edges.map((e) => (
                                    <TableRow key={`${e.ligand_a}->${e.ligand_b}`}>
                                        <TableCell>{e.ligand_a}</TableCell>
                                        <TableCell>{e.ligand_b}</TableCell>
                                        <TableCell align="right">{fmtPm(e.ddg, e.ddg_err)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            <Box sx={{
                display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem',
            }}
            >
                <BMapsTooltip title={applyDisabled || `Write ΔG onto ${candidates.length} workspace compound${candidates.length === 1 ? '' : 's'}`}>
                    {wrapForDisabledTooltip(
                        <DialogPrimaryButton
                            onClick={apply}
                            disabled={!!applyDisabled || applying}
                            sx={{ width: '100%' }}
                        >
                            {applying ? 'Applying…' : 'Apply ΔG to compounds'}
                        </DialogPrimaryButton>
                    )}
                </BMapsTooltip>
                {applied.length > 0 && (
                    <span style={{ fontSize: '0.9rem' }}>
                        {`Applied to ${applied.length} compound${applied.length === 1 ? '' : 's'}`}
                        {missing.length > 0 ? `; not in workspace: ${missing.join(', ')}` : ''}
                    </span>
                )}
                {applyErrors && <ImportErrors errors={applyErrors} />}
            </Box>
        </DialogContentSection>
    );
}
